import { useState, useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    Sparkles,
    ArrowRight,
    Phone,
    Mail,
    Check,
    ShieldCheck,
    Award,
    Users,
    Clock,
    Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/lib/seo";
import { LANDING_PAGES } from "@/lib/landingPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 h-12";

const WHY_ICONS = [Sparkles, ShieldCheck, Award, Heart, Users, Clock];

export default function LandingPage() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
    const data = LANDING_PAGES[slug];

    useSEO({
        title: data?.seoTitle,
        description: data?.seoDescription,
        image: "https://images.unsplash.com/photo-1747696766706-5485b39bf358",
    });

    // Inject Schema.org JSON-LD (FAQ, LegalService, Breadcrumb)
    useEffect(() => {
        if (!data) return;
        const id = `ld-${data.slug}`;
        const existing = document.getElementById(id);
        if (existing) existing.remove();

        const ld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "FAQPage",
                    mainEntity: data.faq.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: {
                            "@type": "Answer",
                            text: f.a,
                        },
                    })),
                },
                {
                    "@type": "LegalService",
                    name: `BM Juridik – ${data.eyebrow}`,
                    description: data.seoDescription,
                    url: `https://bmjuridik.se/${data.slug}`,
                    telephone: "+46108085613",
                    email: "kontakt@bmjuridik.se",
                    areaServed: { "@type": "Country", name: "Sverige" },
                    serviceType: data.eyebrow,
                    ...(data.showFreeBadge && {
                        offers: {
                            "@type": "Offer",
                            name: data.freeBadgeText || "Kostnadsfri inledande rådgivning",
                            price: "0",
                            priceCurrency: "SEK",
                            availability: "https://schema.org/InStock",
                        },
                    }),
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        {
                            "@type": "ListItem",
                            position: 1,
                            name: "Hem",
                            item: "https://bmjuridik.se/",
                        },
                        {
                            "@type": "ListItem",
                            position: 2,
                            name: "Rättsområden",
                            item: "https://bmjuridik.se/tjanster",
                        },
                        {
                            "@type": "ListItem",
                            position: 3,
                            name: data.eyebrow,
                            item: `https://bmjuridik.se/${data.slug}`,
                        },
                    ],
                },
            ],
        };

        const script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById(id);
            if (el) el.remove();
        };
    }, [data]);

    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!data) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Fyll i namn, e-post och meddelande.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, {
                ...form,
                subject: `${data.eyebrow} — förfrågan via landningssida`,
                area: data.eyebrow,
            });
            setDone(true);
            toast.success("Tack! Vi återkommer inom 24 timmar.");
        } catch {
            toast.error("Något gick fel. Försök igen eller ring oss direkt.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid={`page-landing-${data.slug}`} className="bg-[#0A0A0B] text-[#F4F4F5]">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/5">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                        background:
                            "radial-gradient(circle at 25% 30%, #C4A977 0%, transparent 50%)",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        {data.showFreeBadge && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm mb-7">
                                <Sparkles size={12} className="text-[#C4A977]" />
                                <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                                    {data.freeBadgeText || "Kostnadsfri inledande rådgivning"}
                                </span>
                            </div>
                        )}
                        {!data.showFreeBadge && (
                            <p className="overline mb-6">{data.eyebrow}</p>
                        )}
                        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            {data.h1}{" "}
                            <span className="italic text-[#C4A977]">{data.h1Italic}</span>
                        </h1>
                        <p className="mt-7 text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
                            {data.intro}
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            {data.ctaHref.startsWith("#") ? (
                                <a
                                    href={data.ctaHref}
                                    data-testid={`landing-${data.slug}-cta-primary`}
                                    className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group transition-colors"
                                >
                                    {data.primaryCta}
                                    <ArrowRight
                                        size={14}
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                </a>
                            ) : (
                                <Link
                                    to={data.ctaHref}
                                    data-testid={`landing-${data.slug}-cta-primary`}
                                    className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group transition-colors"
                                >
                                    {data.primaryCta}
                                    <ArrowRight
                                        size={14}
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                </Link>
                            )}
                            <a
                                href="tel:+46108085613"
                                className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-white/20 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-sm tracking-wide transition-colors"
                            >
                                <Phone size={14} />
                                010 - 808 56 13
                            </a>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
                            {data.statBlocks.map((s) => (
                                <div key={s.label}>
                                    <p className="font-serif text-3xl text-[#C4A977]">
                                        {s.value}
                                    </p>
                                    <p className="text-xs text-[#A1A1AA] mt-1">
                                        {s.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 hidden lg:block">
                        <div className="relative aspect-[4/5] max-w-md mx-auto">
                            <img
                                src="/team/daniel-madsen.jpg"
                                alt={`Daniel Madsen — jurist inom ${data.eyebrow.toLowerCase()}`}
                                className="w-full h-full object-cover grayscale-[0.2]"
                            />
                            <div className="absolute -top-4 -left-4 w-20 h-20 border-t border-l border-[#C4A977]/60" />
                            <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b border-r border-[#C4A977]/60" />
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="overline">Det här hjälper vi dig med</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Allt inom{" "}
                            <span className="italic text-[#C4A977]">
                                {data.eyebrow.toLowerCase()}
                            </span>
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {data.services.map((s) => (
                            <div
                                key={s.title}
                                className="bg-[#111113] border border-white/5 p-7 rounded-sm hover:border-[#C4A977]/40 transition-colors"
                            >
                                <Check size={18} className="text-[#C4A977]" />
                                <h3 className="mt-4 font-serif text-xl">{s.title}</h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-20 lg:py-24 bg-[#0A0A0B] border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="overline">Så går det till</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Från första samtal{" "}
                            <span className="italic text-[#C4A977]">till lösning.</span>
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {data.process.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="bg-[#111113] border border-white/5 p-7 rounded-sm"
                            >
                                <p className="font-serif text-4xl text-[#C4A977]/40 leading-none">
                                    0{i + 1}
                                </p>
                                <h3 className="mt-5 font-serif text-xl">{s.title}</h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <p className="overline">Varför BM Juridik</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Erfarenhet möter{" "}
                            <span className="italic text-[#C4A977]">precision.</span>
                        </h2>
                        <p className="mt-6 text-[#A1A1AA] leading-relaxed">
                            Daniel Madsen är jurist sedan 2012 och företräder
                            klienter i hela Sverige. Vi kombinerar djup juridisk
                            kunskap med en personlig och tydlig kommunikation —
                            så att du alltid vet var ditt ärende står.
                        </p>
                        <Link
                            to="/medarbetare"
                            className="mt-7 inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                        >
                            Mer om vårt team
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {data.whyUs.map((b, i) => {
                            const Icon = WHY_ICONS[i % WHY_ICONS.length];
                            return (
                                <div
                                    key={b.title}
                                    className="bg-[#111113] border border-white/10 p-5 rounded-sm"
                                >
                                    <Icon size={20} className="text-[#C4A977]" />
                                    <p className="mt-3 text-sm font-semibold text-[#F4F4F5]">
                                        {b.title}
                                    </p>
                                    <p className="mt-1.5 text-xs text-[#A1A1AA] leading-relaxed">
                                        {b.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto">
                        <p className="overline">Vanliga frågor</p>
                        <h2 className="mt-4 font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Det här undrar{" "}
                            <span className="italic text-[#C4A977]">våra klienter.</span>
                        </h2>
                    </div>
                    <div className="mt-14 space-y-4">
                        {data.faq.map((f) => (
                            <details
                                key={f.q}
                                className="group bg-[#111113] border border-white/10 hover:border-[#C4A977]/40 rounded-sm transition-colors"
                            >
                                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 px-6 py-5">
                                    <h3 className="font-serif text-lg text-[#F4F4F5] flex-1">
                                        {f.q}
                                    </h3>
                                    <span className="text-[#C4A977] text-2xl leading-none mt-1 transition-transform group-open:rotate-45">
                                        +
                                    </span>
                                </summary>
                                <p className="px-6 pb-6 text-sm text-[#A1A1AA] leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT FORM */}
            <section
                id="kontakt"
                className="py-20 lg:py-28 bg-[#0A0A0B]"
                data-testid={`landing-${data.slug}-contact`}
            >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-xl mx-auto">
                        <h2 className="font-serif font-light text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                            Boka{" "}
                            <span className="italic text-[#C4A977]">rådgivning</span>
                        </h2>
                        <p className="mt-5 text-[#A1A1AA] leading-relaxed">
                            Beskriv kort din situation så återkommer vi inom 24 timmar.
                        </p>
                    </div>

                    {done ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-12 bg-[#111113] border border-[#C4A977]/40 rounded-sm p-10 text-center"
                            data-testid={`landing-${data.slug}-success`}
                        >
                            <Check size={36} className="text-[#C4A977] mx-auto" />
                            <h3 className="mt-5 font-serif text-2xl">Tack för din förfrågan!</h3>
                            <p className="mt-3 text-[#A1A1AA] leading-relaxed">
                                Vi återkommer personligen inom 24 timmar.
                            </p>
                        </motion.div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="mt-12 bg-[#111113] border border-white/10 rounded-sm p-8 space-y-5"
                            data-testid={`landing-${data.slug}-form`}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                        Namn
                                    </Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        className={`mt-2 ${inputClass}`}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                        E-post
                                    </Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        className={`mt-2 ${inputClass}`}
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                    Telefon (valfritt)
                                </Label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({ ...form, phone: e.target.value })
                                    }
                                    className={`mt-2 ${inputClass}`}
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                    Beskriv din situation
                                </Label>
                                <Textarea
                                    value={form.message}
                                    onChange={(e) =>
                                        setForm({ ...form, message: e.target.value })
                                    }
                                    rows={5}
                                    className={`mt-2 ${inputClass.replace("h-12", "")}`}
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-12 text-sm tracking-wide font-medium"
                            >
                                {submitting ? "Skickar..." : data.primaryCta}
                            </Button>
                            <p className="text-xs text-[#A1A1AA]/70 text-center leading-relaxed">
                                Genom att skicka godkänner du att vi kontaktar dig.
                                Vi behandlar din förfrågan konfidentiellt enligt vår{" "}
                                <Link
                                    to="/integritetspolicy"
                                    className="underline hover:text-[#C4A977]"
                                >
                                    integritetspolicy
                                </Link>
                                .
                            </p>
                        </form>
                    )}

                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center text-sm text-[#A1A1AA]">
                        <a
                            href="tel:+46108085613"
                            className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                        >
                            <Phone size={14} className="text-[#C4A977]" />
                            010 - 808 56 13
                        </a>
                        <span className="hidden sm:block">·</span>
                        <a
                            href="mailto:kontakt@bmjuridik.se"
                            className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                        >
                            <Mail size={14} className="text-[#C4A977]" />
                            kontakt@bmjuridik.se
                        </a>
                    </div>

                    {/* Related links */}
                    {data.relatedPages?.length > 0 && (
                        <div className="mt-16 pt-10 border-t border-white/10 text-center">
                            <p className="overline mb-5">Relaterat</p>
                            <div className="flex flex-wrap gap-3 justify-center">
                                {data.relatedPages.map((r) => (
                                    <Link
                                        key={r.to}
                                        to={r.to}
                                        className="px-4 py-2 text-sm border border-white/10 hover:border-[#C4A977]/40 hover:text-[#C4A977] text-[#A1A1AA] rounded-sm transition-colors"
                                    >
                                        {r.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
